/**
 * WalletKYCTiersPage — citizen KYC tier viewer + upgrade (Spec 15).
 *
 * Live endpoints:
 *   - GET  /v1/wallet/wallets-mine       — show current tier + balance.
 *   - POST /v1/kyc/tier-upgrade-requests — NIN verification + tier promotion.
 *
 * Upgrade flow (TIER_1 → TIER_2):
 *   - Citizen enters 11-digit NIN.
 *   - Submit calls service-kyc which validates NIN (Luhn) + calls NIMC stub.
 *   - On APPROVED: service-kyc calls service-financial-services to promote
 *     wallet tier. UI refreshes wallet query to show new tier.
 *
 * Test NIN that always succeeds: 90000000001 (starts with '9', passes Luhn).
 * Any NIN not starting with '9' returns REJECTED (stub behaviour).
 *
 * Source: `screens/src/pages/nipost-pay/WalletKYCTiers.jsx` (operator-side
 * mock; reshaped for the citizen).
 */
import { useState, type ChangeEvent, type FormEvent, type JSX } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowUpCircle, CheckCircle, CheckCircle2, Loader2, ShieldCheck, XCircle } from 'lucide-react';

import { requestKycTierUpgrade } from '../../lib/api/nipost-pay';
import { useCustomerAuth } from '../../lib/auth/useCustomerAuth';
import {
  KYC_TIER_CAPS,
  KycTierBadge,
  PageHeader,
  customerWalletQueryKey,
  describeWalletError,
  formatNaira,
  useCustomerWallet,
} from './NipostPayUI';

const UPGRADE_REQUIREMENTS: Readonly<Record<string, readonly string[]>> = {
  TIER_0: ['Verify your NIN with NIMC', 'Provide a valid Nigerian phone number'],
  TIER_1: ['Enter your 11-digit National Identification Number (NIN)', 'NIN must pass NIMC verification'],
  TIER_2: ['Add and verify your BVN', 'Confirm address with a recent utility bill'],
  TIER_3: ['You are at the highest tier — no upgrade required.'],
};

export function WalletKYCTiersPage(): JSX.Element {
  const queryClient = useQueryClient();
  const { token, claims } = useCustomerAuth();
  const customerId = claims?.upmpUserId ?? '';
  const wallet = useCustomerWallet(customerId, token);
  const [nin, setNin] = useState<string>('');

  const currentTier = wallet.data?.kycTier ?? 'TIER_0';
  const meta = KYC_TIER_CAPS[currentTier] ?? KYC_TIER_CAPS['TIER_0'] ?? { label: 'Unknown', inflowCap: '0', description: '' };
  const requirements = UPGRADE_REQUIREMENTS[currentTier] ?? UPGRADE_REQUIREMENTS['TIER_0'] ?? [];
  const canUpgrade = currentTier !== 'TIER_3';

  const upgrade = useMutation({
    mutationFn: () =>
      requestKycTierUpgrade(
        { nin: nin.trim() },
        { bearerToken: token ?? undefined },
      ),
    onSuccess: () => {
      // Refresh wallet so the new tier is displayed
      void queryClient.invalidateQueries({ queryKey: customerWalletQueryKey(customerId) });
    },
  });

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (nin.trim().length !== 11) return;
    upgrade.mutate();
  }

  return (
    <div>
      <PageHeader
        title="KYC tier"
        description="Higher tiers unlock larger inflow caps and additional features."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Current tier */}
          <div className="bg-white rounded-xl border border-border p-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-nipost-green" /> Your current tier
            </h3>
            {wallet.isLoading ? (
              <p className="text-sm text-muted inline-flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading…
              </p>
            ) : wallet.isError ? (
              <p className="text-sm text-red-600">{describeWalletError(wallet.error)}</p>
            ) : (
              <div className="flex flex-wrap items-center gap-4">
                <KycTierBadge tier={currentTier} />
                <p className="text-sm text-gray-700">{meta.description}</p>
              </div>
            )}
            {wallet.data ? (
              <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-muted">Daily inflow cap</p>
                  <p className="font-semibold text-gray-900 mt-1">
                    {meta.inflowCap === 'unlimited' ? 'Unlimited' : formatNaira(meta.inflowCap)}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-muted">Inflow used today</p>
                  <p className="font-semibold text-gray-900 mt-1">
                    {formatNaira(wallet.data.dailyInflowToday ?? '0')}
                  </p>
                </div>
              </div>
            ) : null}
          </div>

          {/* Upgrade form */}
          <div className="bg-white rounded-xl border border-border p-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <ArrowUpCircle className="w-5 h-5 text-nipost-green" /> Upgrade requirements
            </h3>
            <ul className="space-y-2 text-sm text-gray-700 mb-4">
              {requirements.map((r) => (
                <li key={r} className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-nipost-green flex-shrink-0 mt-0.5" /> {r}
                </li>
              ))}
            </ul>

            {/* Success / error feedback — rendered OUTSIDE the tier-conditional form
                so it persists even after the wallet query refreshes and changes currentTier. */}
            {upgrade.isError ? (
              <div
                className="mb-4 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700 flex items-center gap-2"
                role="alert"
              >
                <XCircle className="w-3.5 h-3.5 flex-shrink-0" />
                {describeWalletError(upgrade.error)}
              </div>
            ) : null}

            {upgrade.isSuccess ? (
              upgrade.data.status === 'APPROVED' || upgrade.data.status === 'AUTO_APPROVED' ? (
                <div
                  className="mb-4 rounded-lg bg-success-light border border-success/20 px-3 py-2 text-xs text-success flex items-center gap-2"
                  role="status"
                >
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Upgrade approved — your wallet is now <strong>{upgrade.data.targetTier}</strong>!
                  {upgrade.data.ninVerifiedName ? ` (${upgrade.data.ninVerifiedName})` : ''}
                </div>
              ) : (
                <div
                  className="mb-4 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-700 flex items-center gap-2"
                  role="status"
                >
                  <XCircle className="w-3.5 h-3.5 flex-shrink-0" />
                  Upgrade {upgrade.data.status.toLowerCase()}: {upgrade.data.reason ?? 'NIN could not be verified.'}
                </div>
              )
            ) : null}

            {canUpgrade && (currentTier === 'TIER_1' || currentTier === 'TIER_0') && !upgrade.isSuccess ? (
              <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                <div>
                  <label htmlFor="nin-input" className="block text-sm font-medium text-gray-700 mb-1">
                    National Identification Number (NIN)
                  </label>
                  <input
                    id="nin-input"
                    type="text"
                    inputMode="numeric"
                    maxLength={11}
                    value={nin}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setNin(e.target.value.replace(/\D/g, ''))}
                    placeholder="11-digit NIN"
                    disabled={upgrade.isPending}
                    className="w-full sm:w-64 px-3 py-2 border border-border rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-nipost-green/20 focus:border-nipost-green disabled:opacity-60"
                  />
                  <p className="mt-1 text-xs text-muted">
                    Enter your 11-digit NIN as printed on your NIMC slip.
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={nin.trim().length !== 11 || upgrade.isPending}
                  className="px-5 py-2.5 text-sm font-medium text-white bg-nipost-green rounded-lg hover:bg-nipost-green-dark disabled:opacity-50 inline-flex items-center gap-2"
                >
                  {upgrade.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <ArrowUpCircle className="w-4 h-4" />
                  )}
                  {upgrade.isPending ? 'Verifying NIN…' : 'Verify NIN & upgrade'}
                </button>
              </form>
            ) : canUpgrade && !upgrade.isSuccess ? (
              <p className="text-sm text-muted">
                Upgrade from {currentTier} requires additional verification (BVN + address proof). Contact support.
              </p>
            ) : !upgrade.isSuccess ? (
              <p className="text-sm text-nipost-green flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" /> You are at the highest KYC tier.
              </p>
            ) : null}
          </div>
        </div>

        {/* Tier comparison */}
        <div>
          <div className="bg-white rounded-xl border border-border p-5 lg:sticky lg:top-24">
            <h3 className="font-semibold text-gray-900 mb-3">All tiers</h3>
            <ul className="space-y-3">
              {(Object.keys(KYC_TIER_CAPS) as readonly (keyof typeof KYC_TIER_CAPS)[]).map((k) => {
                const t = KYC_TIER_CAPS[k];
                if (!t) return null;
                return (
                  <li key={k} className="border border-border rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <KycTierBadge tier={k} />
                      <span className="text-xs text-muted">
                        {t.inflowCap === 'unlimited' ? 'Unlimited' : formatNaira(t.inflowCap)}
                      </span>
                    </div>
                    <p className="text-xs text-muted mt-1">{t.description}</p>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
